// src/oura/components/visibility/SourcesTab.tsx
//
// Where the answers read about the business: how many cite its own site, and
// the sites they quote instead. Only the business's own pages count as earned;
// a mention that rests on directories and the map is rented, and caps the
// rating at 3 of 5 whatever the mention rate (YW, 4 Sep 2026).

import { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Dot, FOCUS_RING, IconSquare, OfValue, PillButton, Tag, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { MERCHANT_V2 } from "../../merchantV2";
import { countedUnit, countedUnitLabel, joinNames } from "../../lib/oura";
import type { EngineFilter, VisModel, VisSourceRow } from "../../visibility/model";
import { SOURCE_KIND, SOURCE_KIND_ORDER, SOURCE_KIND_WORDS, UNKNOWN_SOURCE, sourceKind } from "./sourceKinds";
import type { AeoSourceKind } from "@service/aeo/types";

/**
 * "14 of 48" where the count is questions of the set, else "14 answers". Out
 * of the headline questions, as the backend counts it: branded and
 * unwinnable questions are not in the source tally.
 */
/** Sources per page, as the questions table pages its rows. */
const SOURCES_PAGE = 10;

function SourceCount({ row, of, basis }: { row: VisSourceRow; of: number; basis: string | null }) {
    const questions = countedUnit(basis) === "question";
    return (
        <>
            <span className={cn(TYPE.tb, "tabular-nums", row.own ? "font-semibold text-blue-600" : "text-gray-900")}>{row.count}</span>
            {(!questions || of > 0) && (
                <span className="hidden text-gray-400 sm:inline">
                    {questions ? ` of ${of}` : ` ${countedUnitLabel(row.count, basis)}`}
                </span>
            )}
        </>
    );
}

export function SourcesTab({
    model,
    staff,
    engine,
    onOpen,
}: {
    model: VisModel;
    staff: boolean;
    /** The page's engine filter. Sources are not split by engine, and say so while one is on. */
    engine: EngineFilter;
    onOpen: (row: VisSourceRow) => void;
}) {
    const s = model.sources;
    // Only when the payload classifies its rows: the own row alone does not
    // earn a column of dashes.
    const typed = s.rows.some((r) => r.kind !== null);
    const unlisted = s.total !== null ? Math.max(0, s.total - s.rows.length) : 0;
    const whose = staff ? "its" : "your";
    // The merchant's list says each site's kind in words, filters by it and
    // keeps the icons grey (redesign). Staff keep the tinted squares.
    const labelled = !staff && MERCHANT_V2;
    const kindOf = (r: VisSourceRow): AeoSourceKind => sourceKind(r) ?? "other";
    const [only, setOnly] = useState<AeoSourceKind | "all">("all");
    // Own site has no filter of its own: it is the highlighted row.
    const kinds = SOURCE_KIND_ORDER.map((k) => ({ k, n: s.rows.filter((r) => !r.own && kindOf(r) === k).length })).filter((x) => x.n > 0);
    const rows = labelled && only !== "all" ? s.rows.filter((r) => !r.own && kindOf(r) === only) : s.rows;
    const [page, setPage] = useState(1);
    // Pages of ten are the merchant redesign's; everyone else gets the whole list.
    const perPage = labelled ? SOURCES_PAGE : Math.max(1, rows.length);
    const pages = Math.max(1, Math.ceil(rows.length / perPage));
    const current = Math.min(page, pages);
    // A new scan, engine or filter is a new list: back to its first page.
    useEffect(() => setPage(1), [s.rows, only]);
    const first = rows.length === 0 ? 0 : (current - 1) * perPage + 1;
    const last = Math.min(current * perPage, rows.length);
    const slice = rows.slice(first - 1, last);
    // How the figure is counted, said under it rather than in a column of
    // its own (redesign, merchant): alone over there it read as a stray line.
    const fromNote = s.from.length > 0 ? (
        <>
            {staff ? `From ${joinNames(s.from)}.` : "Counted from the answers that list their sources."}
            {s.appShowsNoLinks && (staff
                ? " The logged-out ChatGPT app shows no source links."
                : " ChatGPT answers list no sources, so these come from the other assistants.")}
        </>
    ) : null;
    const noteUnder = !staff && MERCHANT_V2;
    const sideColumn = s.own?.count === 0 || s.capped || (!noteUnder && fromNote);

    return (
        <div className="border-t border-gray-100">
            {/* The own-site figure is a strip over the list rather than a
                column beside it: beside a long list it ended in bare white. */}
            <div className="flex flex-wrap items-start gap-x-10 gap-y-3 px-5 py-4">
                <div className="min-w-0">
                    <p className={cn(TYPE.lab, "m-0")}>
                        Answers citing {whose} own site
                        {engine !== "all" && <span className="font-normal text-gray-400"> · All AIs</span>}
                    </p>
                    {s.own ? (
                        <>
                            <p className={cn(TYPE.num, "m-0 mt-1 text-gray-900")}>
                                <OfValue n={s.own.count} of={s.own.of} unit={countedUnitLabel(s.own.of, s.own.basis)} />
                            </p>
                            {/* The share as the live Overview stated it: of the
                                questions (or answers, on an older basis) that could
                                cite a source, how many cite the business's own site. */}
                            <p className={cn(TYPE.tm, "m-0 mt-0.5 text-gray-500")}>
                                <span className="font-medium tabular-nums text-gray-900">{Math.round((s.own.count / s.own.of) * 100)}%</span>
                                {` of the ${s.own.of} ${countedUnitLabel(s.own.of, s.own.basis)} cite ${staff ? "the business's" : "your"} own site`}
                            </p>
                            {noteUnder && fromNote && <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-400")}>{fromNote}</p>}
                        </>
                    ) : s.legacyOwn !== null ? (
                        // An older scan: a real count, but nothing recorded to divide it by.
                        <p className={cn(TYPE.num, "m-0 mt-1 text-gray-900")}>
                            <OfValue n={s.legacyOwn} unit={countedUnitLabel(s.legacyOwn, null)} />
                        </p>
                    ) : (
                        <p className={cn(TYPE.tb, "m-0 mt-1 text-gray-500")}>{s.absent ?? "Not measured on this scan."}</p>
                    )}
                </div>
                {sideColumn && (
                    <div className="min-w-0 flex-1 basis-64 space-y-1.5 sm:pt-1">
                        {s.own?.count === 0 && (
                            <p className={cn(TYPE.tb, "m-0 text-gray-900")}>
                                {staff ? "No answer cited the business's own site." : "No answer cited your own website."}
                            </p>
                        )}
                        {s.capped && (staff ? (
                            <p className={cn(TYPE.tb, "m-0 inline-flex items-center gap-1.5 font-medium text-amber-800")}>
                                <Dot tone="amber" />
                                Rented visibility. Caps the rating at 3 of 5.
                            </p>
                        ) : (
                            <p className={cn(TYPE.tb, "m-0 text-gray-900")}>
                                Most answers quote other sites, not yours. The pages we publish change this.
                            </p>
                        ))}
                        {!noteUnder && fromNote && <p className={cn(TYPE.tm, "m-0 text-gray-400")}>{fromNote}</p>}
                    </div>
                )}
            </div>
            <div className="min-w-0 border-t border-gray-100">
                {s.rows.length === 0 ? (
                    <p className={cn(TYPE.tb, "m-0 px-5 py-8 text-gray-500")}>No answer quoted a website on this scan.</p>
                ) : (
                    <>
                    {labelled && kinds.length > 1 && (
                        // Pill filters (style guide 6b), each with its count.
                        <div className="flex flex-wrap items-center gap-1 px-5 py-3">
                            {[{ k: "all" as const, n: s.rows.length }, ...kinds].map(({ k, n }) => {
                                const on = only === k;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => setOnly(k)}
                                        className={cn(
                                            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors",
                                            on ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                            FOCUS_RING,
                                        )}
                                    >
                                        {k === "all" ? "All" : SOURCE_KIND_WORDS[k].plural}
                                        <span className={cn("ml-1 tabular-nums", on ? "text-blue-100" : "text-gray-400")}>{n}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <table className={cn(TYPE.tc, "w-full table-fixed border-collapse")}>
                        {/* On a phone the domain needs the width: the type
                            column goes, and the count drops its "of 50". */}
                        <colgroup>
                            <col />
                            {/* Hidden on a phone with its cells, so the count keeps
                                the last column instead of falling into this one. */}
                            {(typed || labelled) && <col className="hidden sm:table-column sm:w-36" />}
                            <col className="w-[88px] sm:w-[104px]" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col" className="py-2.5 pl-5 pr-2 text-left font-medium text-gray-500">Source</th>
                                {(typed || labelled) && <th scope="col" className="hidden px-2 py-2.5 text-left font-medium text-gray-500 sm:table-cell">Type</th>}
                                <th scope="col" className="py-2.5 pl-2 pr-5 text-right font-medium text-gray-500">
                                    {countedUnit(s.basis) === "question" ? "Questions" : "Answers"}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {slice.map((row) => {
                                const kind = sourceKind(row);
                                const k = kind ? SOURCE_KIND[kind] : null;
                                return (
                                    <tr
                                        key={row.domain}
                                        data-source-row={row.domain}
                                        onClick={() => onOpen(row)}
                                        // The client's own site is the one row with colour.
                                        className={cn("cursor-pointer", labelled && row.own ? "bg-blue-50/60 hover:bg-blue-50" : "hover:bg-gray-50")}
                                    >
                                        <td className="h-[60px] border-t border-gray-100 py-2 pl-5 pr-2">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onOpen(row); }}
                                                className={cn("flex w-full min-w-0 items-center gap-3 rounded text-left", FOCUS_RING)}
                                            >
                                                <IconSquare tone={labelled ? "gray" : (k ?? UNKNOWN_SOURCE).tone} icon={(k ?? UNKNOWN_SOURCE).icon} />
                                                {labelled ? (
                                                    <span className="min-w-0">
                                                        <span className={cn(TYPE.tb, "block truncate font-medium", row.own ? "text-blue-600" : "text-gray-900")}>{row.domain}</span>
                                                        <span className="block truncate text-xs text-gray-500">{SOURCE_KIND_WORDS[kindOf(row)].note}</span>
                                                    </span>
                                                ) : (
                                                    <span className={cn(TYPE.tb, "min-w-0 truncate font-medium", row.own ? "text-blue-600" : "text-gray-900")}>
                                                        {row.domain}
                                                    </span>
                                                )}
                                                {/* Said in words, not by colour alone, and outside the
                                                    truncated name so a phone keeps it. Beside a Type
                                                    column that already says it, only on a phone. */}
                                                {row.own && !labelled && (
                                                    <span className={cn(TYPE.tb, "-ml-2 shrink-0 whitespace-nowrap text-gray-400", typed && "sm:hidden")}>
                                                        {staff ? "(own site)" : "(your site)"}
                                                    </span>
                                                )}
                                            </button>
                                        </td>
                                        {labelled ? (
                                            <td className="hidden border-t border-gray-100 px-2 text-left sm:table-cell">
                                                {row.own ? <Tag tone="blue">Your site</Tag> : <Tag tone="gray">{SOURCE_KIND[kindOf(row)].label}</Tag>}
                                            </td>
                                        ) : typed && (
                                            <td className="hidden border-t border-gray-100 px-2 text-left text-gray-500 sm:table-cell">
                                                {k ? (row.own && !staff ? "Your site" : k.label) : <span className="text-gray-300">–</span>}
                                            </td>
                                        )}
                                        <td className="border-t border-gray-100 py-2 pl-2 pr-5 text-right">
                                            <SourceCount row={row} of={model.headline.of} basis={s.basis} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </>
                )}
                {pages > 1 && (
                    <div className={cn(TYPE.tc, "flex items-center justify-between gap-4 border-t border-gray-100 px-5 py-3 text-gray-500")}>
                        <span className="tabular-nums">
                            {first}–{last} of {rows.length}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <PillButton size="sm" icon={ChevronLeftIcon} aria-label="Previous page" disabled={current <= 1} onClick={() => setPage(current - 1)} />
                            <span className="tabular-nums">
                                {current} of {pages}
                            </span>
                            <PillButton size="sm" icon={ChevronRightIcon} aria-label="Next page" disabled={current >= pages} onClick={() => setPage(current + 1)} />
                        </span>
                    </div>
                )}
                {/* The payload lists the top sites and counts the rest; there
                    are no rows behind this line to open. */}
                {staff && unlisted > 0 && (
                    <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 px-5 py-3 text-gray-400")}>
                        And {unlisted} more {unlisted === 1 ? "site" : "sites"}, each quoted less often.
                    </p>
                )}
            </div>
        </div>
    );
}
